import { auth } from '@/lib';
import {
  ActionType,
  type ApigeeError,
  ErrorCodes,
  type EventParameters,
  ExceptionType,
  logger,
  type NOSIDError,
  useI18n,
  useToast,
} from '@nosinovacao/nosid-mfe-common';
import {
  type MutationOptions,
  type queryOptions,
  type infiniteQueryOptions,
  type InfiniteData,
  useMutation,
  useQuery,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { useEffect, useState } from 'react';

// Prevent showing the same error toast multiple times when multiple components use the same query
const shownErrorsByQueryKey = new Set<string>();

export interface CaptchaState {
  challenge: boolean;
  token: string | null | undefined;
  setToken: (token: string | null | undefined) => void;
}

export function showErrorToast(
  errorCode: ErrorCodes | null,
  toast: ReturnType<typeof useToast>,
  t: ReturnType<typeof useI18n>['t'],
  format: 'id+message' | 'message' | 'id',
) {
  const errorIdKey = `Errors.${errorCode}.Id`;
  const errorMessageKey = `Errors.${errorCode}.Message`;
  const errorId = t(errorIdKey);
  const errorMessage = t(errorMessageKey);
  const message =
    errorMessage === errorMessageKey ? t('Errors.DefaultError.Message').toString() : errorMessage;
  const id = errorId === errorIdKey ? t('Errors.DefaultError.Id') : errorId;
  toast.show(
    'error',
    [format === 'id' ? null : message, format === 'message' ? null : id]
      .filter(Boolean)
      .join(' - '),
  );
}

function handleError(
  res: AxiosError<NOSIDError | ApigeeError>,
  toast: ReturnType<typeof useToast>,
  t: ReturnType<typeof useI18n>['t'],
  ignoreSnackbarForErrors: Array<ErrorCodes | '*'> = [],
): 'Unauthenticated' | 'Challenge' | 'AnyError' {
  const bodyError = res.response?.data as NOSIDError | null;
  const tokenInvalidError = res.response?.data as ApigeeError;
  if (
    res.status === 401 &&
    tokenInvalidError?.fault?.detail?.errorcode?.length &&
    tokenInvalidError?.fault?.faultstring?.length
  ) {
    void auth.logout();
    return 'Unauthenticated';
  }

  if (bodyError?.ErrorCode === ErrorCodes.Challenge) {
    return 'Challenge';
  }

  if (
    !ignoreSnackbarForErrors.includes('*') &&
    (!bodyError?.ErrorCode || !ignoreSnackbarForErrors.includes(bodyError?.ErrorCode))
  ) {
    showErrorToast(bodyError?.ErrorCode ?? null, toast, t, 'id+message');
  }

  return 'AnyError';
}

export function useNosIdQuery<T, TError, TQueryKey extends readonly unknown[]>(
  options: ReturnType<typeof queryOptions<T, TError, T, TQueryKey>>,
) {
  const [token, setToken] = useState<string | null | undefined>(null);
  const [challenge, setChallenge] = useState(false);
  const queryRes = useQuery(options);
  const toast = useToast();
  const { t } = useI18n();

  useEffect(() => {
    setChallenge(false);
    const queryKeyStr = JSON.stringify(options.queryKey);

    if (queryRes.status !== 'error') {
      shownErrorsByQueryKey.delete(queryKeyStr);
      return;
    }

    if (shownErrorsByQueryKey.has(queryKeyStr)) {
      return;
    }
    shownErrorsByQueryKey.add(queryKeyStr);
    const exceptionsShouldThrow = options.throwOnError ?? true;
    const res = handleError(
      queryRes.error as AxiosError<NOSIDError | ApigeeError>,
      toast,
      t,
      exceptionsShouldThrow ? ['*'] : [],
    );
    setChallenge(() => res === 'Challenge');

    if (res === 'AnyError' && exceptionsShouldThrow) {
      throw queryRes.error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryRes.status]);
  return {
    ...queryRes,
    captcha: { challenge, token, setToken } as CaptchaState,
    isLoadingData: queryRes.isLoading || !queryRes.isEnabled,
  };
}

interface NosIdMutationOptions<T, TResult> {
  ignoreLogging?: boolean;
  successLogAction?: (input: T, result: TResult) => EventParameters;
  errorLogAction?: (input: T, error: NOSIDError) => EventParameters;
  ignoreSnackbarForErrors?: Array<ErrorCodes | '*'>;
}

export function useNosIdMutation<T = unknown, TVariables = void, TError = Error>({
  mutationFn,
  onSuccess,
  onError,
  ignoreLogging,
  successLogAction,
  errorLogAction,
  ignoreSnackbarForErrors,
  ...options
}: MutationOptions<T, TError, TVariables> & NosIdMutationOptions<TVariables, T>) {
  const [token, setToken] = useState<string | null | undefined>(null);
  const [challenge, setChallenge] = useState(false);
  const toast = useToast();
  const { t } = useI18n();

  const mutationResult = useMutation({
    ...options,
    mutationFn: ((...args: Parameters<NonNullable<typeof mutationFn>>) => {
      setChallenge(() => false);
      setToken(() => null);
      return mutationFn?.(...args);
    }) as NonNullable<typeof mutationFn>,
    onSuccess: (...args: Parameters<NonNullable<typeof onSuccess>>) => {
      if (!ignoreLogging) {
        logMutationEvent(
          successLogAction?.(args[1], args[0]) ?? {
            ActionType: ActionType.Submit,
            Option: '',
          },
        );
      }
      return onSuccess?.(...args);
    },
    onError: ((...args: Parameters<NonNullable<typeof onError>>) => {
      const err = args[0];
      const errorHandlingResult = handleError(
        err as AxiosError<NOSIDError | ApigeeError>,
        toast,
        t,
        ignoreSnackbarForErrors,
      );
      if (errorHandlingResult === 'Unauthenticated') return onError?.(...args);
      setChallenge(() => errorHandlingResult === 'Challenge');
      const errorBody = (err as AxiosError<NOSIDError>).response?.data;
      if (!errorBody) return onError?.(...args);
      if (!ignoreLogging) {
        logMutationEvent(
          errorLogAction?.(args[1], errorBody) ?? {
            ActionType: ActionType.Submit,
            Option: '',
          },
          errorBody,
        );
      }
      return onError?.(...args);
    }) as NonNullable<typeof onError>,
  });
  return { ...mutationResult, captcha: { challenge, token, setToken } as CaptchaState };
}

export function logMutationEvent(
  eventParameters: EventParameters,
  error: NOSIDError | null = null,
): void {
  if (error) {
    eventParameters.ExceptionType = ExceptionType.Server;
    eventParameters.ExceptionMessage = error?.ErrorCode
      ? [error.ErrorCode]
      : (error?.Params?.map(({ ErrorCode }) => ErrorCode) ?? []);

    logger.sendExceptionEvent(eventParameters);
    return;
  }
  switch (eventParameters.ActionType) {
    case ActionType.ChangeContact:
    case ActionType.Signup:
    case ActionType.PasswordRecovery:
    case ActionType.Cancel:
    case ActionType.SocialNetworks:
      logger.sendScreenNavigationEvent(eventParameters);
      break;
    default:
      logger.sendScreenActionEvent(eventParameters);
  }
}

export function useNosIdInfiniteQuery<T, TError, TQueryKey extends readonly unknown[], TPageParams>(
  options: ReturnType<
    typeof infiniteQueryOptions<T, TError, InfiniteData<T>, TQueryKey, TPageParams>
  >,
) {
  const [token, setToken] = useState<string | null | undefined>(null);
  const [challenge, setChallenge] = useState(false);
  const infiniteQueryRes = useInfiniteQuery(options);
  const toast = useToast();
  const { t } = useI18n();

  useEffect(() => {
    setChallenge(false);
    const queryKeyStr = JSON.stringify(options.queryKey);

    if (infiniteQueryRes.status !== 'error') {
      shownErrorsByQueryKey.delete(queryKeyStr);
      return;
    }

    if (shownErrorsByQueryKey.has(queryKeyStr)) {
      return;
    }
    shownErrorsByQueryKey.add(queryKeyStr);
    const exceptionsShouldThrow = options.throwOnError ?? true;
    const res = handleError(
      infiniteQueryRes.error as AxiosError<NOSIDError | ApigeeError>,
      toast,
      t,
      exceptionsShouldThrow ? ['*'] : [],
    );
    setChallenge(() => res === 'Challenge');

    if (res === 'AnyError' && exceptionsShouldThrow) {
      throw infiniteQueryRes.error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infiniteQueryRes.status]);
  return {
    ...infiniteQueryRes,
    captcha: { challenge, token, setToken } as CaptchaState,
  };
}
